/**
 * OEMUtils - Utility functions for OEM placeholder replacement
 * Centralizes all OEM branding replacement logic to avoid duplication
 */

import { oemConfig } from './OEMConfig';
import { AppInfo } from './AppInfo';

/**
 * Standard OEM placeholders used in HTML templates
 */
export interface OEMPlaceholders {
  OEM_LOGO: string;
  OEM_LOGIN_LOGO: string;
  OEM_CAPTION: string;
  OEM_FULLNAME: string;
  OEM_WEBSITE: string;
  OEM_VERSION: string;
  OEM_LOGIN_BACKGROUND: string;
  OEM_LOGIN_THEME: string;
  OEM_COMPANY_NAME: string;
  OEM_COMPANY_LOGO: string;
  OEM_COMPANY_WEBSITE: string;
  OEM_COPYRIGHT: string;
}

/**
 * Get all OEM placeholder values
 */
export function getOEMPlaceholders(): OEMPlaceholders {
  const splashImage = oemConfig.getSplashImage();
  const loginLogo = oemConfig.getLoginLogo() || splashImage;
  const company = oemConfig.getCompany();

  return {
    OEM_LOGO: loginLogo,
    OEM_LOGIN_LOGO: loginLogo,
    OEM_CAPTION: oemConfig.getBrandName(),
    OEM_FULLNAME: oemConfig.getFullBrandName(),
    OEM_WEBSITE: oemConfig.getWebsite(),
    OEM_VERSION: AppInfo.getVersion(),
    OEM_LOGIN_BACKGROUND: oemConfig.getLoginBackground() || '',
    OEM_LOGIN_THEME: oemConfig.getLoginTheme(),
    OEM_COMPANY_NAME: company.name,
    OEM_COMPANY_LOGO: company.logo || '',
    OEM_COMPANY_WEBSITE: company.website,
    OEM_COPYRIGHT: company.copyright
  };
}

/**
 * Replace all OEM placeholders in an HTML string
 * @param html The HTML string containing {OEM_*} placeholders
 * @returns The HTML string with all placeholders replaced
 */
export function replaceOEMPlaceholders(html: string): string {
  const placeholders = getOEMPlaceholders();

  let result = html;
  for (const [key, value] of Object.entries(placeholders)) {
    const regex = new RegExp(`\\{${key}\\}`, 'g');
    result = result.replace(regex, value);
  }

  return result;
}

/**
 * Fetch an HTML template and replace OEM placeholders
 * @param templatePath Path to the HTML template (e.g., '/forms/auth/login.html')
 * @returns The processed HTML string
 * @throws Error if template cannot be loaded
 */
export async function loadOEMTemplate(templatePath: string): Promise<string> {
  const response = await fetch(templatePath);
  if (!response.ok) {
    throw new Error(`Failed to load template: ${templatePath} (${response.status})`);
  }

  const html = await response.text();
  return replaceOEMPlaceholders(html);
}
